import React, { forwardRef } from 'react'
import { Dropdown } from 'react-bootstrap'
import './MessageSortDropdown.css'

export type MessageSortMenuItem = {
  id: string
  label: string
}

export type MessageSortMenuSection = {
  id: string
  label: string
  items: MessageSortMenuItem[]
  /** Currently selected item id in this section, or null. */
  selectedId: string | null
}

export type MessageSortFooterItem = {
  id: string
  label: string
}

type MessageSortDropdownProps = {
  sections: MessageSortMenuSection[]
  onSelect: (sectionId: string, itemId: string) => void
  footerItems?: MessageSortFooterItem[]
  onFooterSelect?: (itemId: string) => void
  title?: string
  disabled?: boolean
  className?: string
}

type SortToggleProps = {
  children: React.ReactNode
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  disabled?: boolean
}

const SortToggle = forwardRef<HTMLButtonElement, SortToggleProps>(
  ({ children, onClick, disabled }, ref) => (
    <button
      ref={ref}
      type="button"
      className="msg-sort-toggle"
      disabled={disabled}
      onClick={(e) => {
        e.preventDefault()
        onClick(e)
      }}
    >
      {children}
    </button>
  ),
)
SortToggle.displayName = 'MessageSortToggle'

function MessageSortDropdown({
  sections,
  onSelect,
  footerItems,
  onFooterSelect,
  title = 'Sort by',
  disabled = false,
  className = '',
}: MessageSortDropdownProps): React.ReactElement {
  return (
    <Dropdown className={`msg-sort-dropdown ${className}`.trim()}>
      <Dropdown.Toggle as={SortToggle} disabled={disabled} title={title}>
        <span className="msg-sort-toggle-label">Sort by</span>
        <i
          className="mgc mgc_down_small_fill msg-sort-toggle-chevron"
          aria-hidden
        />
      </Dropdown.Toggle>
      <Dropdown.Menu className="msg-sort-dropdown-menu" align="end">
        {sections.map((section, sectionIndex) => (
          <React.Fragment key={section.id}>
            {sectionIndex > 0 ? <div className="msg-sort-divider" /> : null}
            <div className="msg-sort-section-label">{section.label}</div>
            {section.items.map((item) => {
              const isActive = section.selectedId === item.id
              return (
                <Dropdown.Item
                  key={item.id}
                  className={isActive ? 'msg-sort-active' : ''}
                  onClick={() => onSelect(section.id, item.id)}
                >
                  <span className="msg-sort-check-col">
                    {isActive ? (
                      <i className="mgc mgc_check_line msg-sort-check" />
                    ) : null}
                  </span>
                  {item.label}
                </Dropdown.Item>
              )
            })}
          </React.Fragment>
        ))}
        {footerItems && footerItems.length > 0 ? (
          <>
            <div className="msg-sort-divider" />
            {footerItems.map((item) => (
              <Dropdown.Item
                key={item.id}
                className="msg-sort-footer-item"
                onClick={() => onFooterSelect?.(item.id)}
              >
                <span className="msg-sort-check-col" />
                {item.label}
              </Dropdown.Item>
            ))}
          </>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default MessageSortDropdown
